/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';
import {
	TEudrRegParseRequest,
	TEudrRegParseResponsePayload,
	TEudrRegisterRequest,
	TEudrPlansResponsePayload,
	TEudrServiceStatusesResponsePayload,
	TEudrItemsRequest,
	TEudrItemsResponsePayload,
	TGenerateEudrInvoiceRequest,
	TDownloadEudrLastInvoiceRequest,
	TDownloadEudrLastInvoiceResponsePayload,
	TEudrActivateRequest,
} from './types';
import {
	regParseEudrRequest,
	regParseEudrSuccess,
	regParseEudrFailure,
	registerEudrRequest,
	registerEudrSuccess,
	registerEudrFailure,
	getEudrPlansRequest,
	getEudrPlansSuccess,
	getEudrServiceStatusesRequest,
	getEudrServiceStatusesSuccess,
	getEudrItemsRequest,
	getEudrItemsSuccess,
	generateInvoiceEudrRequest,
	generateInvoiceEudrSuccess,
	downloadEudrLastInvoiceRequest,
	downloadEudrLastInvoiceSuccess,
	activateEudrRequest,
	activateEudrSuccess,
	activateEudrFailure,
} from './reducer';

export function* regParseEudrRequestWorker(action: PayloadAction<TEudrRegParseRequest>) {
	const { payload } = action;
	try {
		const response: TEudrRegParseResponsePayload = yield call(api.eudr.regParseEudr, payload);
		yield put(regParseEudrSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message = String(error?.response?.data?.message ?? error ?? 'Error!');
			notificationContainer(message, 'error');
			yield put(regParseEudrFailure({ error: message }));
		}
	}
}

export function* registerEudrRequestWorker(action: PayloadAction<TEudrRegisterRequest>) {
	const { payload } = action;
	try {
		yield call(api.eudr.registerEudr, payload);
		yield put(registerEudrSuccess());
		notificationContainer('Registration successful', 'success');
		setTimeout(() => {
			window.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message = String(error?.response?.data?.message ?? error ?? 'Error!');
			notificationContainer(message, 'error');
			yield put(registerEudrFailure({ error: message }));
		}
	}
}

export function* getEudrPlansRequestWorker() {
	try {
		const response: TEudrPlansResponsePayload = yield call(api.eudr.getEudrPlans);
		yield put(getEudrPlansSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getEudrServiceStatusesRequestWorker() {
	try {
		const response: TEudrServiceStatusesResponsePayload = yield call(
			api.eudr.getEudrServiceStatuses,
		);
		yield put(getEudrServiceStatusesSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* getEudrItemsRequestWorker(action: PayloadAction<TEudrItemsRequest>) {
	const { payload } = action;
	try {
		const response: TEudrItemsResponsePayload = yield call(api.eudr.getEudrItems, payload);
		yield put(getEudrItemsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* generateInvoiceEudrRequestWorker(
	action: PayloadAction<TGenerateEudrInvoiceRequest>,
) {
	const { payload } = action;
	try {
		yield call(api.eudr.generateInvoiceEudr, payload);
		yield put(generateInvoiceEudrSuccess());
		notificationContainer('Invoice generated successfully', 'success');
		setTimeout(() => {
			window.location.reload();
		}, 1000);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error?.response?.data?.message ?? error ?? 'Error!'), 'error');
		}
	}
}

export function* downloadEudrLastInvoiceRequestWorker(
	action: PayloadAction<TDownloadEudrLastInvoiceRequest>,
): Generator<any, void, any> {
	const { payload } = action;
	try {
		const response: TDownloadEudrLastInvoiceResponsePayload = yield call(
			api.eudr.downloadEudrLastInvoice,
			payload,
		);
		if (response) {
			fileDownload(response, `invoice_${String(payload.id)}.pdf`);
		}
		yield put(downloadEudrLastInvoiceSuccess());
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.status === 404) {
				notificationContainer('File not found', 'error');
			} else {
				const responseData = yield error?.response?.data.text().then((data: any) => data);
				notificationContainer(JSON.parse(responseData)?.message ?? 'File download error', 'error');
			}
		}
		yield put(downloadEudrLastInvoiceSuccess());
	}
}

export function* activateEudrRequestWorker(action: PayloadAction<TEudrActivateRequest>) {
	const { payload } = action;
	try {
		yield call(api.eudr.activateEudr, payload);
		yield put(activateEudrSuccess());
		notificationContainer('Activated successfully', 'success');
		yield put(
			getEudrItemsRequest({
				filters: [{ fieldName: 'emId', value: String(payload.id), operator: '=' }],
			}),
		);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const message = String(error?.response?.data?.message ?? error ?? 'Error!');
			notificationContainer(message, 'error');
			yield put(activateEudrFailure({ error: message }));
		}
	}
}

export function* eudrSaga() {
	yield takeEvery(regParseEudrRequest.type, regParseEudrRequestWorker);
	yield takeEvery(registerEudrRequest.type, registerEudrRequestWorker);
	yield takeEvery(getEudrPlansRequest.type, getEudrPlansRequestWorker);
	yield takeEvery(getEudrServiceStatusesRequest.type, getEudrServiceStatusesRequestWorker);
	yield takeEvery(getEudrItemsRequest.type, getEudrItemsRequestWorker);
	yield takeEvery(generateInvoiceEudrRequest.type, generateInvoiceEudrRequestWorker);
	yield takeEvery(downloadEudrLastInvoiceRequest.type, downloadEudrLastInvoiceRequestWorker);
	yield takeEvery(activateEudrRequest.type, activateEudrRequestWorker);
}
